import dayjs from "dayjs";
import {
  type AllDataReportConfigurationInput,
  type ElvacoX109ReportConfiguration,
  type ReportExecutionOut,
  outputTypeEnum,
} from "kubb";
import { useState } from "react";
import { StackedListTwoColumnItem } from "../../../components/Theme/stacked-list";
import ReportExecutionArtifactsDownloadButton from "./ReportExecutionArtifactsDownloadButton";
import ReportExecutionCalculatingBadge from "./ReportExecutionCalculatingBadge";
import usePollingRefresh from "./usePollingRefresh";

import advancedFormat from "dayjs/plugin/advancedFormat";
import { lowerCase, startCase } from "lodash-es";

dayjs.extend(advancedFormat);

function formatEnum(value: string | undefined | null): string {
  return value ? startCase(lowerCase(value)) : "";
}

function description(execution: ReportExecutionOut): string {
  const config = execution.report_format.configuration as any;
  const parts: string[] = [];

  switch (execution.report_format.format) {
    case outputTypeEnum.AGGREGATED_DATA:
      if (config?.aggregation) {
        parts.push(config.aggregation.parameter);
        parts.push(formatEnum(config.aggregation.aggregation));
      }
      if (config?.period) parts.push(formatEnum(config.period));
      if (config?.view_by) parts.push(`View by: ${formatEnum(config.view_by)}`);
      break;
    case outputTypeEnum.ALL_DATA:
      if (config?.time_filter)
        parts.push(
          `Time Filter: ${(config as AllDataReportConfigurationInput).time_filter}`,
        );
      if (config?.data_format) parts.push(formatEnum(config.data_format));
      if (config?.file_type) parts.push(config.file_type);
      break;
    case outputTypeEnum.ELVACO_X109:
    case outputTypeEnum.ELVACO_X110:
      parts.push(
        `Time Filter: ${(config as ElvacoX109ReportConfiguration).time_filter}`,
      );
      break;
    case outputTypeEnum.CUMULATIVE_DATA:
      if (config?.missing_data_handling)
        parts.push(`Missing data: ${formatEnum(config.missing_data_handling)}`);
      break;
  }

  if (execution.scheme_id) {
    parts.push(`Scheme: ${execution.scheme_id}`);
  }

  return parts.join(" | ");
}

export default function ReportExecutionListRow(props: {
  execution: ReportExecutionOut;
  refresh: () => void;
}) {
  const { execution, refresh } = props;
  const calculating = execution.status === "CALCULATING";
  const [wasCalculating, _0] = useState<boolean>(calculating);

  usePollingRefresh(calculating, refresh);

  const name = formatEnum(execution.report_format.format);
  const dates = `${
    execution.start_date
      ? dayjs(execution.start_date).format("Do MMM YYYY")
      : dayjs(execution.end_date).format("Do MMM YYYY")
  } - ${dayjs(execution.end_date).format("Do MMM YYYY")}`;

  const desc = description(execution);

  return (
    <StackedListTwoColumnItem
      column1={
        <div className="min-w-0 flex-auto">
          <div>
            <span className={"inline-block w-60"}>
              {name}
              <ReportExecutionCalculatingBadge
                execution={execution}
                wasCalculating={wasCalculating}
                className={"px-2 mx-2"}
              />
            </span>
            <span className={"inline-block w-60"}>{dates}</span>
            <span className={"inline-block italic text-gray-600"}>
              Created: {dayjs(execution.created_at).format("Do MMM YYYY HH:mm")}
            </span>
          </div>
          {desc && <div className="text-xs text-gray-500 pt-0.5">{desc}</div>}
        </div>
      }
      column2={<ReportExecutionArtifactsDownloadButton execution={execution} />}
      column1Size={"sm:w-11/12"}
      column2Size={"sm:w-1/12"}
    />
  );
}
